import { useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ModuleRegistry,
  type SortChangedEvent,
} from 'ag-grid-community';
import { trendzGridTheme } from '@components/grid/trendzGridTheme';
import '@components/grid/agGridTheme.css';
import { buildApmcsColumns } from '../columns';
import {
  APMC_SORT_KEYS,
  type Apmc,
  type ApmcSortKey,
  type SortDirection,
} from '../schema';
import type { CommonResponse } from '@/types/api';

ModuleRegistry.registerModules([AllCommunityModule]);

interface Props {
  data: CommonResponse<Apmc> | undefined;
  onEdit: (row: Apmc) => void;
  onSortChange: (sort: { sortBy?: ApmcSortKey; sortDir?: SortDirection }) => void;
}

const DEFAULT_COL_DEF = {
  resizable: true,
  sortable: false,
  filter: false,
  minWidth: 100,
  unSortIcon: true,
};

const SORT_KEY_SET = new Set<string>(APMC_SORT_KEYS);

function readSort(event: SortChangedEvent): {
  sortBy?: ApmcSortKey;
  sortDir?: SortDirection;
} {
  const state = event.api.getColumnState();
  const active = state.find(
    (c) => c.sort != null && c.colId && SORT_KEY_SET.has(c.colId),
  );
  if (!active || !active.sort || !active.colId) return {};
  return {
    sortBy: active.colId as ApmcSortKey,
    sortDir: active.sort as SortDirection,
  };
}

export function ApmcsGrid({ data, onEdit, onSortChange }: Props) {
  const columnDefs = useMemo(() => buildApmcsColumns({ onEdit }), [onEdit]);

  return (
    <div className="h-full w-full">
      <AgGridReact<Apmc>
        theme={trendzGridTheme}
        rowData={data?.rowData ?? []}
        columnDefs={columnDefs}
        defaultColDef={DEFAULT_COL_DEF}
        animateRows
        getRowId={(p) => p.data.id}
        suppressCellFocus
        suppressMultiSort
        rowHeight={56}
        onSortChanged={(e) => onSortChange(readSort(e))}
      />
    </div>
  );
}
